import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, Send, X, Copy, CreditCard, FileDown, Undo2 } from "lucide-react";
import { getInvoice, sendInvoice, voidInvoice, duplicateInvoice, recordPayment } from "@/lib/accounting/accounting.functions";
import { refundInvoicePayment } from "@/lib/payments.functions";
import { getStripeEnvironment, paymentsConfigured } from "@/lib/stripe";
import { fmtMoney, fmtDate } from "@/lib/accounting/exports";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/accounting/invoices/$id")({
  component: InvoiceDetail,
});

function InvoiceDetail() {
  const { id } = Route.useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const getFn = useServerFn(getInvoice);
  const sendFn = useServerFn(sendInvoice);
  const voidFn = useServerFn(voidInvoice);
  const dupFn = useServerFn(duplicateInvoice);
  const payFn = useServerFn(recordPayment);
  const refundFn = useServerFn(refundInvoicePayment);
  const { data, isLoading } = useQuery({ queryKey: ["accounting", "invoice", id], queryFn: () => getFn({ data: { id } }) });

  const [payOpen, setPayOpen] = useState(false);
  const [pAmt, setPAmt] = useState(0);
  const [pMethod, setPMethod] = useState<"cash" | "check" | "ach" | "zelle" | "venmo" | "credit_card_external" | "quickbooks" | "other">("check");
  const [pCat, setPCat] = useState<"deposit" | "progress" | "final" | "refund" | "credit">("progress");
  const [pRef, setPRef] = useState("");
  const [pNotes, setPNotes] = useState("");

  if (isLoading || !data) return <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>;

  const { invoice, line_items, payments } = data;

  const refresh = () => qc.invalidateQueries({ queryKey: ["accounting", "invoice", id] });

  const send = async () => {
    try { await sendFn({ data: { id } }); toast.success("Invoice sent"); refresh(); }
    catch (e) { toast.error((e as Error).message); }
  };
  const doVoid = async () => {
    const reason = window.prompt("Reason for voiding?") ?? "";
    if (reason === null) return;
    try { await voidFn({ data: { id, reason } }); toast.success("Voided"); refresh(); }
    catch (e) { toast.error((e as Error).message); }
  };
  const dup = async () => {
    try { const r = await dupFn({ data: { id } }); toast.success("Duplicated"); nav({ to: "/admin/accounting/invoices/$id", params: { id: r.id } }); }
    catch (e) { toast.error((e as Error).message); }
  };
  const savePay = async () => {
    if (!pAmt) { toast.error("Amount required"); return; }
    try {
      await payFn({ data: { invoice_id: id, amount: pAmt, method: pMethod, category: pCat, reference: pRef || null, notes: pNotes || null } });
      toast.success("Payment recorded");
      setPayOpen(false); setPAmt(0); setPRef(""); setPNotes("");
      refresh();
    } catch (e) { toast.error((e as Error).message); }
  };

  const doRefund = async (paymentId: string, maxAmount: number) => {
    if (!paymentsConfigured()) { toast.error("Online payments are not configured"); return; }
    const input = window.prompt(`Refund amount (max ${fmtMoney(maxAmount)}). Leave blank to refund in full.`, "");
    if (input === null) return;
    const amt = input.trim() === "" ? undefined : Number(input);
    if (amt !== undefined && (!Number.isFinite(amt) || amt <= 0 || amt > maxAmount)) {
      toast.error("Invalid amount"); return;
    }
    const reason = window.prompt("Refund reason (optional)", "") ?? "";
    try {
      const r = await refundFn({ data: { payment_id: paymentId, amount: amt, reason: reason || undefined, environment: getStripeEnvironment() } });
      if ("error" in r) { toast.error(r.error); return; }
      toast.success("Refund issued");
      refresh();
    } catch (e) { toast.error((e as Error).message); }
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/admin/accounting/invoices" className="text-xs text-zinc-500 hover:text-white">← Invoices</Link>
          <h2 className="font-serif text-2xl text-white mt-1">{invoice.invoice_number}</h2>
          <p className="text-sm text-zinc-400">{invoice.customer_name} {invoice.property_address ? `· ${invoice.property_address}` : ""}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {invoice.status === "draft" && (
            <button onClick={send} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-300 text-zinc-950 text-xs font-semibold"><Send className="size-3.5" /> Send</button>
          )}
          <button onClick={() => setPayOpen(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-400/30 text-emerald-300 text-xs font-medium hover:bg-emerald-400/10"><CreditCard className="size-3.5" /> Record payment</button>
          <button onClick={dup} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-zinc-300 text-xs hover:bg-white/5"><Copy className="size-3.5" /> Duplicate</button>
          <Link to="/admin/accounting/invoices/$id/print" params={{ id }} target="_blank" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-zinc-300 text-xs hover:bg-white/5"><FileDown className="size-3.5" /> PDF / Print</Link>
          {invoice.status !== "void" && (
            <button onClick={doVoid} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-red-400/30 text-red-300 text-xs hover:bg-red-500/10"><X className="size-3.5" /> Void</button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-3 text-sm">
        <Tile label="Status" value={invoice.status.replace("_", " ")} />
        <Tile label="Issued" value={fmtDate(invoice.issue_date)} />
        <Tile label="Due" value={fmtDate(invoice.due_date)} />
        <Tile label="Balance due" value={fmtMoney(invoice.balance_due)} accent />
      </div>

      <section className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/10">
            <tr><th className="text-left px-4 py-2">Item</th><th className="text-left px-4 py-2">Description</th><th className="text-right px-4 py-2">Qty</th><th className="text-right px-4 py-2">Rate</th><th className="text-right px-4 py-2">Amount</th></tr>
          </thead>
          <tbody>
            {line_items.map((l: any) => (
              <tr key={l.id} className="border-b border-white/5">
                <td className="px-4 py-2 text-zinc-400 capitalize">{l.kind.replace("_", " ")}</td>
                <td className="px-4 py-2 text-white">{l.description}</td>
                <td className="px-4 py-2 text-right text-zinc-300">{Number(l.quantity)}</td>
                <td className="px-4 py-2 text-right text-zinc-300">{fmtMoney(l.unit_price)}</td>
                <td className="px-4 py-2 text-right text-white">{fmtMoney(l.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-4 py-3 space-y-1 text-sm max-w-xs ml-auto">
          <Row k="Subtotal" v={fmtMoney(invoice.subtotal)} />
          <Row k={`Tax (${(Number(invoice.tax_rate) * 100).toFixed(2)}%)`} v={fmtMoney(invoice.tax_amount)} />
          <Row k="Total" v={fmtMoney(invoice.total)} bold />
          <Row k="Paid" v={fmtMoney(invoice.amount_paid)} />
          <Row k="Balance due" v={fmtMoney(invoice.balance_due)} bold />
        </div>
      </section>

      <section>
        <h3 className="text-sm font-medium text-white mb-2">Payments</h3>
        {payments.length === 0 ? (
          <p className="text-xs text-zinc-500">No payments recorded yet.</p>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] divide-y divide-white/5">
            {payments.map((p: any) => (
              <div key={p.id} className="px-4 py-3 grid grid-cols-[1fr_auto] gap-2 text-sm">
                <div>
                  <p className="text-white">{fmtMoney(p.amount)} <span className="text-zinc-500 capitalize text-xs">· {p.method?.replace?.("_", " ")} · {(p.category ?? p.kind)}</span></p>
                  <p className="text-[11px] text-zinc-500">{fmtDate(p.recorded_at)} {p.reference ? `· ${p.reference}` : ""}</p>
                  {p.notes ? <p className="text-[11px] text-zinc-500 mt-1">{p.notes}</p> : null}
                </div>
                {p.provider === "stripe" && p.category !== "refund" && p.kind !== "refund" ? (
                  <button
                    onClick={() => doRefund(p.id, Number(p.amount))}
                    className="self-start text-[11px] px-3 py-1 rounded-full border border-white/10 text-zinc-200 hover:bg-white/5 inline-flex items-center gap-1"
                    title="Issue refund via Stripe"
                  >
                    <Undo2 className="size-3" /> Refund
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </section>

      {payOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" onClick={() => setPayOpen(false)}>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 max-w-md w-full space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-lg text-white">Record payment</h3>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Amount</span>
              <input type="number" step="0.01" value={pAmt || ""} onChange={(e) => setPAmt(Number(e.target.value))} className={inputCls} />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Method</span>
                <select value={pMethod} onChange={(e) => setPMethod(e.target.value as any)} className={inputCls}>
                  {["check", "ach", "zelle", "venmo", "cash", "credit_card_external", "quickbooks", "other"].map((m) => (
                    <option key={m} value={m}>{m.replace("_", " ")}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Category</span>
                <select value={pCat} onChange={(e) => setPCat(e.target.value as any)} className={inputCls}>
                  {["deposit", "progress", "final", "refund", "credit"].map((m) => (<option key={m} value={m}>{m}</option>))}
                </select>
              </label>
            </div>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Reference #</span>
              <input value={pRef} onChange={(e) => setPRef(e.target.value)} className={inputCls} />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Notes</span>
              <textarea value={pNotes} onChange={(e) => setPNotes(e.target.value)} rows={2} className={inputCls} />
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setPayOpen(false)} className="text-xs px-4 py-2 rounded-full border border-white/10 text-zinc-300">Cancel</button>
              <button onClick={savePay} className="text-xs px-4 py-2 rounded-full bg-emerald-400 text-zinc-950 font-semibold">Record</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-amber-300/40";

function Tile({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3">
      <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</p>
      <p className={`mt-1 font-serif text-lg ${accent ? "text-amber-300" : "text-white"}`}>{value}</p>
    </div>
  );
}

function Row({ k, v, bold }: { k: string; v: string; bold?: boolean }) {
  return (
    <div className={`flex justify-between ${bold ? "text-white font-semibold border-t border-white/10 pt-1.5 mt-1.5" : "text-zinc-400"}`}>
      <span>{k}</span><span>{v}</span>
    </div>
  );
}